/**
 * Byte helpers for crossing into WebCrypto.
 *
 * TypeScript 5.7 made `Uint8Array` generic over its buffer type, and
 * `Uint8Array<ArrayBufferLike>` is not assignable to `BufferSource` — because
 * the buffer *could* be a `SharedArrayBuffer`, which WebCrypto will not accept.
 *
 * Asserting the type away works, but the assertion is a claim about runtime
 * state rather than a fact the compiler can check. Copying into a plain
 * `ArrayBuffer` is checkable, and the copy is not worth worrying about at the
 * sizes this project handles (10 MB images at the very most).
 */

/** Copy `bytes` into a plain `ArrayBuffer` that WebCrypto accepts. */
export function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);

  return copy.buffer;
}

/** Lowercase hex encoding. */
export function toHex(bytes: Uint8Array): string {
  let out = "";

  for (const byte of bytes) {
    out += byte.toString(16).padStart(2, "0");
  }

  return out;
}

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/**
 * Base64-encode a UTF-8 string.
 *
 * Uses `btoa` rather than Node's `Buffer`: this runs in Workers too, where
 * `Buffer` does not exist. The intermediate `String.fromCharCode` loop is what
 * makes it safe for multi-byte characters — `btoa` alone only accepts latin1.
 */
export function toBase64(value: string): string {
  const bytes = textEncoder.encode(value);
  let binary = "";

  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary);
}

/** Decode a base64 string back to UTF-8. */
export function fromBase64(value: string): string {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return textDecoder.decode(bytes);
}
